import {
  forwardRef,
  type HTMLAttributes,
} from 'react';

import {
  cva,
  type VariantProps,
} from 'class-variance-authority';

import { cn } from '../../../utils/styles/cn';

/**
 * <TooltipSection> — one slot of a tooltip body. Multiple sections can
 * be stacked; the outer TooltipBody rounds the first and last.
 *
 * Variants: small | large (size) and primary | main | secondary | error | inverse
 * (background/text color).
 */

const tooltipSectionVariants = cva(
  'px-t4 py-t3 bg-gray-0 text-gray-7',
  {
    variants: {
      size: {
        small: 'px-t3 py-t2 text-t1',
        large: 'px-t4 py-t3',
      },
      variant: {
        primary: 'bg-primary-4 text-white',
        main: 'bg-white',
        secondary: 'bg-gray-0',
        destructive: 'bg-error-4 text-white',
        inverse: 'bg-gray-7 text-gray-0',
      },
    },
  },
);

export type TooltipSectionVariants = VariantProps<typeof tooltipSectionVariants>;

export interface TooltipSectionProps
  extends HTMLAttributes<HTMLDivElement>,
    TooltipSectionVariants {
  /** @deprecated Use size="small". */
  small?: boolean;
  /** @deprecated Use size="large". */
  large?: boolean;
  /** @deprecated Use variant="primary". */
  primary?: boolean;
  /** @deprecated Use variant="main". */
  main?: boolean;
  /** @deprecated Use variant="secondary". */
  secondary?: boolean;
  /** @deprecated Use variant="destructive". */
  error?: boolean;
  /** @deprecated Use variant="inverse". */
  inverse?: boolean;
}

const TooltipSection = forwardRef<HTMLDivElement, TooltipSectionProps>(
  function TooltipSection(
    {
      className,
      size,
      variant,
      small,
      large,
      primary,
      main,
      secondary,
      error,
      inverse,
      ...props
    },
    ref,
  ) {
    const resolvedSize: TooltipSectionVariants['size'] =
      size
      ?? (small && 'small')
      ?? (large && 'large')
      ?? undefined;

    const resolvedVariant: TooltipSectionVariants['variant'] =
      variant
      ?? (primary && 'primary')
      ?? (main && 'main')
      ?? (secondary && 'secondary')
      ?? (error && 'destructive')
      ?? (inverse && 'inverse')
      ?? undefined;

    return (
      <div
        ref={ref}
        className={cn(
          tooltipSectionVariants({ size: resolvedSize, variant: resolvedVariant }),
          className,
        )}
        {...props}
      />
    );
  },
);

export { TooltipSection };
